'use client'

import { useState } from 'react'
import { elements, Element } from '@/data/elements'
import { Button } from './ui/button'
import { Badge } from './ui/badge'
import { X, Plus, GitCompare, Sparkles } from 'lucide-react'
import { useTranslations, useLocale } from 'next-intl'
import { getLocalizedElementData } from '@/data/elements-en'

interface PropertyRow {
  key: string
  label: string
  getValue: (element: Element) => string | number
  format?: (value: string | number) => string
  highlight?: boolean
}

export function ElementComparator() {
  const [selectedElements, setSelectedElements] = useState<Element[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const t = useTranslations('Comparator')
  const tEl = useTranslations('Element')
  const locale = useLocale()
  const localName = (el: typeof elements[0]) =>
    getLocalizedElementData(el.atomicNumber, locale)?.name ?? el.name

  const properties: PropertyRow[] = [
    {
      key: 'symbol',
      label: t('propSymbol'),
      getValue: (el) => el.symbol,
      highlight: true
    },
    {
      key: 'name',
      label: t('propName'),
      getValue: (el) => localName(el),
      highlight: true
    },
    {
      key: 'atomicNumber',
      label: t('propAtomicNumber'),
      getValue: (el) => el.atomicNumber,
    },
    {
      key: 'atomicMass',
      label: t('propAtomicMass'),
      getValue: (el) => el.atomicWeight,
      format: (val) => `${Number(val).toFixed(3)} u`
    },
    {
      key: 'electronConfig',
      label: t('propElectronConfig'),
      getValue: (el) => el.electronConfiguration,
    },
    {
      key: 'category',
      label: t('propCategory'),
      getValue: (el) => el.category.replace(/_/g, ' '),
    },
    {
      key: 'state',
      label: t('propState'),
      getValue: (el) => el.state,
      format: (val) => {
        const states: Record<string, string> = {
          solid: tEl('stateSolid'),
          liquid: tEl('stateLiquid'),
          gas: tEl('stateGas'),
        }
        return states[val as string] || val.toString()
      }
    },
    {
      key: 'period',
      label: t('propPeriod'),
      getValue: (el) => el.period || '-',
    },
    {
      key: 'density',
      label: t('propDensity'),
      getValue: (el) => el.density || '-',
      format: (val) => val === '-' ? '-' : `${val} g/cm³`
    },
    {
      key: 'meltingPoint',
      label: t('propMeltingPoint'),
      getValue: (el) => el.meltingPoint || '-',
      format: (val) => val === '-' ? '-' : `${val} K`
    },
    {
      key: 'boilingPoint',
      label: t('propBoilingPoint'),
      getValue: (el) => el.boilingPoint || '-',
      format: (val) => val === '-' ? '-' : `${val} K`
    },
    {
      key: 'electronegativity',
      label: t('propElectronegativity'),
      getValue: (el) => el.electronegativity || '-',
    },
    {
      key: 'discovery',
      label: t('propDiscovery'),
      getValue: (el) => el.yearDiscovered || '-',
      format: (val) => val === '-' ? '-' : `${val}`
    },
    {
      key: 'discoveredBy',
      label: t('propDiscoveredBy'),
      getValue: (el) => el.discoveredBy || '-',
    },
    {
      key: 'isotopeCount',
      label: t('propIsotopeCount'),
      getValue: (el) => el.isotopes?.length || 0,
    }
  ]

  const addElement = (element: Element) => {
    if (selectedElements.length >= 4) {
      return
    }
    if (!selectedElements.find(el => el.id === element.id)) {
      setSelectedElements([...selectedElements, element])
      setSearchTerm('')
    }
  }

  const removeElement = (elementId: string) => {
    setSelectedElements(selectedElements.filter(el => el.id.toString() !== elementId))
  }

  const filteredElements = elements.filter(el =>
    !selectedElements.find(sel => sel.id === el.id) &&
    (el.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      localName(el).toLowerCase().includes(searchTerm.toLowerCase()) ||
      el.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
      el.atomicNumber.toString().includes(searchTerm))
  ).slice(0, 8)

  return (
    <div className="w-full">
      {/* Element Selection */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <GitCompare className="w-5 h-5 text-[#64ffda]" />
          <h3 className="text-xl font-bold text-white">
            {t('title')}
          </h3>
        </div>

        {/* Selected Elements */}
        <div className="flex flex-wrap gap-3 mb-4">
          {selectedElements.map((element) => (
            <div
              key={element.id}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border-2"
              style={{
                borderColor: element.color,
                backgroundColor: `${element.color}20`
              }}
            >
              <span
                className="font-bold text-2xl"
                style={{ color: element.color }}
              >
                {element.symbol}
              </span>
              <span className="text-white font-medium">{localName(element)}</span>
              <button
                onClick={() => removeElement(element.id.toString())}
                className="ml-2 p-1 hover:bg-red-500/20 rounded-full transition-colors"
              >
                <X size={16} className="text-red-500" />
              </button>
            </div>
          ))}
          {selectedElements.length < 4 && (
            <div className="flex items-center px-4 py-2 border-2 border-dashed border-[#233554] rounded-lg text-[#a8b2d1]">
              <Plus size={20} className="mr-2" />
              {selectedElements.length === 0
                ? t('addElement')
                : t('addMore', { count: 4 - selectedElements.length })}
            </div>
          )}
        </div>

        {/* Search */}
        {selectedElements.length < 4 && (
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('searchPlaceholder')}
              className="w-full px-4 py-3 bg-[#0a192f] border-2 border-[#233554] rounded-lg text-white placeholder-[#a8b2d1] focus:outline-none focus:border-[#64ffda] transition-colors"
            />

            {searchTerm && filteredElements.length > 0 && (
              <div className="absolute z-10 w-full mt-2 bg-[#112240] border-2 border-[#233554] rounded-lg max-h-64 overflow-y-auto">
                {filteredElements.map((element) => (
                  <button
                    key={element.id}
                    onClick={() => addElement(element)}
                    className="w-full px-4 py-3 text-left hover:bg-[#233554] transition-colors flex items-center gap-3 border-b border-[#233554] last:border-b-0"
                  >
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center font-bold text-xl"
                      style={{
                        backgroundColor: `${element.color}20`,
                        color: element.color
                      }}
                    >
                      {element.symbol}
                    </div>
                    <div className="flex-1">
                      <div className="text-white font-semibold">{localName(element)}</div>
                      <div className="text-sm text-[#a8b2d1]">
                        {t('atomicNumber', { num: element.atomicNumber })} • {element.category.replace(/_/g, ' ')}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Comparison Table */}
      {selectedElements.length >= 2 && (
        <div className="overflow-x-auto">
          <div className="bg-[#112240] border-2 border-[#233554] rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-6 py-4 bg-[#0a192f] border-b-2 border-[#233554]">
              <Sparkles className="w-5 h-5 text-[#64ffda]" />
              <h3 className="text-lg font-bold text-[#64ffda]">
                {t('propertiesTitle')}
              </h3>
            </div>

            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-[#233554]">
                  <th className="px-6 py-4 text-left text-[#a8b2d1] font-semibold bg-[#0a192f]/50">
                    {t('property')}
                  </th>
                  {selectedElements.map((element) => (
                    <th
                      key={element.id}
                      className="px-6 py-4 text-center bg-[#0a192f]/50"
                      style={{ minWidth: '200px' }}
                    >
                      <div
                        className="text-4xl font-bold mb-2"
                        style={{
                          color: element.color,
                          textShadow: `0 0 20px ${element.color}80`
                        }}
                      >
                        {element.symbol}
                      </div>
                      <div className="text-sm text-white font-medium">
                        {localName(element)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {properties.map((prop, index) => (
                  <tr
                    key={index}
                    className="border-b border-[#233554] last:border-b-0 hover:bg-[#0a192f]/30 transition-colors"
                  >
                    <td className="px-6 py-4 text-[#a8b2d1] font-medium">
                      {prop.label}
                    </td>
                    {selectedElements.map((element) => {
                      const value = prop.getValue(element)
                      const displayValue = prop.format ? prop.format(value) : value

                      return (
                        <td
                          key={element.id}
                          className={`px-6 py-4 text-center ${
                            prop.highlight
                              ? 'text-white font-bold text-lg'
                              : 'text-[#a8b2d1]'
                          }`}
                        >
                          {prop.highlight && prop.key === 'symbol' ? (
                            <span style={{ color: element.color }}>
                              {displayValue}
                            </span>
                          ) : (
                            displayValue
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {selectedElements.length < 2 && (
        <div className="text-center py-16 bg-[#112240] border-2 border-[#233554] rounded-xl">
          <GitCompare className="w-16 h-16 text-[#233554] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            {t('emptyTitle')}
          </h3>
          <p className="text-[#a8b2d1]">
            {t('emptyHint')}
          </p>
        </div>
      )}
    </div>
  )
}
